import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { sort, uniq } from 'remeda';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, startWith } from 'rxjs/operators';
import { StatementService } from '../../settlement-statement/statement.service';
import { getTracker } from '../../shared/track-by.pipe';
import { SettlementStatement, SettlementStatementDirectDriverPayment } from '../../types';

@Component({
  selector: 'lohi-settlement',
  templateUrl: './settlement.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettlementComponent implements OnInit {
  public driverSelectFormControl = new FormControl('All Drivers');
  public statement$: Observable<SettlementStatement>;
  public driversOnStatement$: Observable<string[]>;
  public filteredStatement$: Observable<SettlementStatement>;
  public totalDeductions$: Observable<number>;
  public locallyCalculatedTotalOfLoads$: Observable<number>;
  public trackDirectDriverPay = getTracker<SettlementStatementDirectDriverPayment>('description');

  constructor(private statementService: StatementService, private route: ActivatedRoute) {}

  public ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const settlementId = params.get('id');
      console.log('Received Settlement ID:', settlementId);

      if (settlementId) {
        const id = Number(settlementId);

        this.statement$ = this.statementService.getStatement$(id).pipe(filter((value) => !!value));

        this.statement$.subscribe((value) => {
          console.log('Statement data received:', value);
        });
      }
    });

    this.totalDeductions$ = this.statement$.pipe(
      map((value) => {
        return (
          (value.statement.nonOrderLineItems || []).reduce(
            (previousValue, currentValue) => previousValue + currentValue.totalInPennies,
            0,
          ) +
          (value.statement.adjustment || []).reduce(
            (previousValue, currentValue) =>
              previousValue + (currentValue.totalInPennies < 0 ? currentValue.totalInPennies : 0),
            0,
          )
        );
      }),
    );

    this.setupDriversOnStatement();
    this.setupFilteredStatement();
  }

  private setupFilteredStatement() {
    const valueChanges$ = this.driverSelectFormControl.valueChanges.pipe(startWith('All Drivers'), shareReplay(1));
    this.filteredStatement$ = combineLatest([this.statement$, valueChanges$]).pipe(
      map(([statement, driverName]) => {
        if (!statement) {
          return statement;
        }
        if (driverName && driverName !== 'All Drivers' && statement.statement?.orders?.length) {
          return {
            ...statement,
            statement: {
              ...statement.statement,
              orders: statement.statement.orders.filter((o) => o.driverName === driverName),
            },
          };
        }
        return statement;
      }),
    );
    this.locallyCalculatedTotalOfLoads$ = this.filteredStatement$.pipe(
      map((statement) => {
        if (!statement || !statement.statement) {
          return 0;
        }
        return statement.statement.orders.reduce((acc, o) => {
          return acc + o.loadTotalInPennies;
        }, 0);
      }),
    );
  }

  private setupDriversOnStatement() {
    this.driversOnStatement$ = this.statement$.pipe(
      map((statement) => {
        const drivers: string[] = [];
        (statement.statement?.orders || []).forEach((order) => {
          drivers.push(order.driverName);
        });
        const uniqueDrivers = uniq(drivers);
        const sortedDrivers = sort(uniqueDrivers, (a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()));
        return sortedDrivers;
      }),
    );
  }
}
